import React, { useState, useEffect, useRef } from 'react';

const AnimatedCounter = ({ target, label, duration = 2000 }) => {
	const [count, setCount] = useState(0);
	const [isVisible, setIsVisible] = useState(false);
	const elementRef = useRef(null);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting && !isVisible) {
					setIsVisible(true);
				}
			},
			{ threshold: 0.1 }
		);

		if (elementRef.current) {
			observer.observe(elementRef.current);
		}

		return () => {
			if (elementRef.current) {
				observer.unobserve(elementRef.current);
			}
		};
	}, [isVisible]);

	useEffect(() => {
		if (!isVisible) return;

		// Extract numeric value from target (e.g., "650" from "650", "45g" from "45g")
		const numericValue = parseFloat(target.replace(/[^\d.]/g, ''));
		if (isNaN(numericValue)) return;

		const startTime = Date.now();
		const startValue = 0;

		const animate = () => {
			const elapsed = Date.now() - startTime;
			const progress = Math.min(elapsed / duration, 1);

			// Easing function for smooth animation
			const easeOutQuart = 1 - Math.pow(1 - progress, 4);
			const currentValue = startValue + (numericValue - startValue) * easeOutQuart;

			setCount(Math.floor(currentValue));

			if (progress < 1) {
				requestAnimationFrame(animate);
			} else {
				setCount(numericValue);
			}
		};

		requestAnimationFrame(animate);
	}, [isVisible, target, duration]);

	// Format the display value with original unit
	const formatValue = (value) => {
		const unit = target.replace(/[\d.]/g, '');
		return unit ? `${value}${unit}` : value.toString();
	};

	return (
		<div ref={elementRef} className="bg-light rounded p-3">
			<strong className="text-primary">{formatValue(count)}</strong>
			<br />
			<small className="text-muted">{label}</small>
		</div>
	);
};

export default AnimatedCounter;
